// src/components/report_details/Subdomains/Subdomains.tsx
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Subdomain } from '../../../types/interfaces';

interface Domain {
  id: number;
  domain_name: string;
}

interface SubdomainsProps {
  selectedDomain: string | null;
  subdomainsData: Subdomain[];    // Ahora Subdomain[]
  domains: Domain[];
  onDomainChange: (domain: string) => void;
}

const Subdomains: React.FC<SubdomainsProps> = ({
  selectedDomain,
  subdomainsData,
  domains,
  onDomainChange,
}) => {
  const { t } = useTranslation();

  // Ordenamos alfabéticamente por subdomain
  const sortedSubdomains = useMemo(() => {
    return [...subdomainsData].sort((a, b) =>
      a.subdomain.localeCompare(b.subdomain)
    );
  }, [subdomainsData]);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const dataPerPage = 9;
  const indexOfLast = currentPage * dataPerPage;
  const indexOfFirst = indexOfLast - dataPerPage;
  const currentData = sortedSubdomains.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedSubdomains.length / dataPerPage);

  const goToNextPage = () =>
    setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
  const goToPreviousPage = () =>
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));

  // Manejador de cambio de dominio en el select
  const handleDomainSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onDomainChange(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
      {/* Header */}
      <div className="flex items-center mb-6">
        <div className="w-3 h-10 bg-gray-800 mr-4"></div>
        <h3 className="text-3xl font-bold">{t('subdomains.title')}</h3>
      </div>
      <p className="text-gray-700 mb-4 text-xl">
        {t('subdomains.description')}
      </p>

      {/* Select de dominios */}
      <div className="mb-4">
        <label
          htmlFor="domain-select"
          className="block mb-2 text-lg font-medium"
        >
          <strong>{t('subdomains.select_domain')}</strong>
        </label>
        <select
          id="domain-select"
          value={selectedDomain || ''}
          onChange={handleDomainSelect}
          className="w-full max-w-md p-2 border border-gray-800 bg-gray-800 text-white font-bold rounded-md"
        >
          {domains.map((d) => (
            <option key={d.id} value={d.domain_name}>
              {d.domain_name}
            </option>
          ))}
        </select>
      </div>

      {/* Contenido: tabla o mensaje de vacío */}
      {sortedSubdomains.length === 0 ? (
        <p className="text-center">{t('subdomains.no_subdomains')}</p>
      ) : (
        <>
          <table className="w-full table-auto mb-4">
            <thead>
              <tr className="bg-gray-200">
                <th className="px-4 py-2 text-left">
                  {t('subdomains.column.subdomain')}
                </th>
                <th className="px-4 py-2 text-left">
                  {t('subdomains.column.source')}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((item, idx) => (
                <tr
                  key={idx}
                  className="border-b hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-2">{item.subdomain}</td>
                  <td className="px-4 py-2">{item.source}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Paginación */}
          <div className="mt-6 flex justify-center items-center space-x-4">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
            >
              {t('subdomains.pagination.previous')}
            </button>
            <span>
              {t('subdomains.pagination.info', {
                current: currentPage,
                total: totalPages,
              })}
            </span>
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
            >
              {t('subdomains.pagination.next')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Subdomains;
